import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { Message, MessageRecipient, MessageAttachment } from "../API";
import {
  type LegacyMessageListItem,
  isMessageEditableByStatus,
} from "../types/message";
import { convertUtcToLocal } from "../utilities/utilities";
import { queryKeys } from "../graphql/queryKeys";
import MessageViewShell, { MessageViewAttachment } from "./MessageViewShell";
import { fetchMessageById } from "../graphql/fetchers";
import BlockingPageLoader from "./ui/BlockingPageLoader";

function isDetailedMessage(
  message: LegacyMessageListItem | Message | null,
): message is Message {
  return !!message && "body" in message && typeof message.body === "string";
}

function hasHydratedMessageRecipients(
  message: LegacyMessageListItem | Message | null,
): boolean {
  const items = message?.Recipients?.items;
  if (!Array.isArray(items)) {
    return false;
  }

  return items.every((item) => !item || !!(item as any).recipient?.id);
}

function hasHydratedMessageAttachments(
  message: LegacyMessageListItem | Message | null,
): boolean {
  const items = message?.Attachments?.items;
  if (!Array.isArray(items)) {
    return false;
  }

  return items.every((item) => !item || !!(item as any).attachment?.id);
}

function isReadyMessageDetail(
  message: LegacyMessageListItem | Message | null,
): message is Message {
  return (
    isDetailedMessage(message) &&
    hasHydratedMessageRecipients(message) &&
    hasHydratedMessageAttachments(message)
  );
}

function LegacyMessageView() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { message: routeMessageId } = useParams<{ message?: string }>();
  const { t } = useTranslation("common");
  const stateMessage =
    (location.state as LegacyMessageListItem | Message | null) ?? null;
  const messageId = (routeMessageId ?? stateMessage?.id ?? "").trim();
  const sourceMessage =
    !messageId || stateMessage?.id === messageId ? stateMessage : null;
  const cachedDetailMessage = useMemo(() => {
    if (!messageId) {
      return null;
    }

    return (
      queryClient.getQueryData<Message | null>(
        queryKeys.messageDetail(messageId),
      ) ?? null
    );
  }, [messageId, queryClient]);
  const routeDetailMessage =
    isReadyMessageDetail(sourceMessage) && sourceMessage.id === messageId
      ? sourceMessage
      : null;
  const cachedReadyDetailMessage =
    isReadyMessageDetail(cachedDetailMessage) &&
    cachedDetailMessage.id === messageId
      ? cachedDetailMessage
      : null;
  const [detailMessage, setDetailMessage] = useState<Message | null>(
    routeDetailMessage ?? cachedReadyDetailMessage ?? null,
  );
  const [detailFetchCompleted, setDetailFetchCompleted] = useState(
    !messageId || !!routeDetailMessage || !!cachedReadyDetailMessage,
  );
  const resolvedDetailMessage = isReadyMessageDetail(detailMessage)
    ? detailMessage
    : routeDetailMessage ?? cachedReadyDetailMessage ?? null;
  const displayedMessage =
    resolvedDetailMessage ?? (detailFetchCompleted ? sourceMessage : null);

  const lastEditedLabel = useMemo(() => {
    const ts: any =
      (displayedMessage as any)?.updatedAt ??
      (displayedMessage as any)?.createdAt ??
      null;
    if (!ts) return null;
    try {
      return convertUtcToLocal(ts, "ShowDayLong");
    } catch {
      return ts;
    }
  }, [displayedMessage]);

  const deliveryValue = useMemo(() => {
    if (displayedMessage?.endOfLifeTrigger) return t("common.endOfLife");
    if (displayedMessage?.scheduledDate) {
      const local = convertUtcToLocal(
        displayedMessage?.scheduledDate,
        "ShowDayLong",
      );
      return local || t("common.fixedDate");
    }
    return t("common.notScheduled");
  }, [displayedMessage, t]);

  const recipientsValue = useMemo(() => {
    const items = displayedMessage?.Recipients?.items ?? [];
    const names = items
      .filter((x): x is MessageRecipient => !!x && !!(x as any).recipient)
      .map((x) => {
        const r: any = (x as any).recipient;
        return (
          r?.name ||
          `${r?.firstName ?? ""} ${r?.lastName ?? ""}`.trim() ||
          r?.email?.trim() ||
          r?.id
        );
      })
      .filter(Boolean);

    return names.length
      ? names.join(", ")
      : t("recipient.onScreenMessage.noRecipients");
  }, [displayedMessage, t]);

  const attachments = useMemo<MessageViewAttachment[]>(() => {
    const items = displayedMessage?.Attachments?.items ?? [];
    return items
      .filter((x): x is MessageAttachment => !!x && !!(x as any).attachment)
      .map((x) => {
        const a: any = (x as any).attachment;
        const fileName =
          String(a?.name ?? "").trim() ||
          (a?.s3Key ? String(a.s3Key).split("/").pop() : "") ||
          a?.id;

        return {
          id: a?.id ?? (x as any).id,
          fileName,
          fileType: a?.type ?? null,
          fileSize: a?.size ?? null,
          s3Key: a?.s3Key ?? "",
        };
      })
      .filter((a) => !!a.id);
  }, [displayedMessage]);

  useEffect(() => {
    let mounted = true;

    if (routeDetailMessage) {
      setDetailMessage(routeDetailMessage);
      setDetailFetchCompleted(true);
      queryClient.setQueryData(
        queryKeys.messageDetail(messageId),
        routeDetailMessage,
      );
      return () => {
        mounted = false;
      };
    }

    if (cachedReadyDetailMessage) {
      setDetailMessage(cachedReadyDetailMessage);
      setDetailFetchCompleted(true);
      return () => {
        mounted = false;
      };
    }

    setDetailMessage(null);
    setDetailFetchCompleted(!messageId);

    if (messageId) {
      fetchMessageById(messageId)
        .then((m) => {
          if (mounted) {
            setDetailMessage(m);
            setDetailFetchCompleted(true);
            if (m?.id) {
              queryClient.setQueryData(queryKeys.messageDetail(m.id), m);
            }
          }
        })
        .catch(() => {
          if (mounted) {
            setDetailFetchCompleted(true);
          }
        });
    } else {
      setDetailFetchCompleted(true);
    }
    return () => {
      mounted = false;
    };
  }, [
    cachedReadyDetailMessage,
    messageId,
    queryClient,
    routeDetailMessage,
  ]);

  if (!messageId) {
    return <Navigate to="/Panel?selectedTab=legacyMessages" replace />;
  }

  if (!resolvedDetailMessage && !detailFetchCompleted) {
    return <BlockingPageLoader message={t("message.loadingData")} />;
  }

  if (!displayedMessage && detailFetchCompleted) {
    return <Navigate to="/Panel?selectedTab=legacyMessages" replace />;
  }

  const resolvedMessage = displayedMessage!;
  const onBack = () => navigate(-1);
  const onEdit = () =>
    navigate(`/MessageEdit/${resolvedMessage.id}`, { state: resolvedMessage });

  const editDisabled = !isMessageEditableByStatus(
    detailMessage?.status ?? resolvedMessage?.status,
  );

  return (
    <MessageViewShell
      variant={resolvedMessage?.sentDate ? "sent" : "legacy"}
      title={
        resolvedMessage?.title ?? t("message.onScreenMessage.untitledMessage")
      }
      lastEditedLabel={lastEditedLabel}
      deliveryLabel={
        resolvedMessage?.sentDate
          ? t("message.deliveredOn")
          : t("common.scheduledToSend")
      }
      deliveryValue={deliveryValue}
      recipientsLabel={t("message.tableColumn.recipients")}
      recipientsValue={recipientsValue}
      bodyHtml={
        isDetailedMessage(displayedMessage) ? displayedMessage.body ?? "" : ""
      }
      attachments={attachments}
      onBack={onBack}
      onEdit={onEdit}
      editDisabled={editDisabled}
    />
  );
}

export default LegacyMessageView;
